/**
 * TownScene.ts - Town exploration scene
 * Handles player movement in towns, NPC interactions, and building entry
 */

import { Scene, GameInput, GameKey } from './types/engine';
import { Renderer } from './Renderer';
import { SpriteManager } from './SpriteManager';
import { CharacterSprite, Direction } from './CharacterSprite';
import { NPCManager } from './NPCManager';
import { Location as StoryLocation } from './data/StoryData';
import {
  TownDefinition,
  TownTile,
  Building,
  BuildingType,
  isTownTileWalkable,
  isBuildingEntrance,
  getBuildingAt,
  isExit,
  getExitAt,
} from './data/TownData';

interface PlayerState {
  x: number;
  y: number;
  tileX: number;
  tileY: number;
  facing: 'up' | 'down' | 'left' | 'right';
  moving: boolean;
  moveProgress: number;
}

export class TownScene implements Scene {
  private renderer: Renderer;
  private spriteManager: SpriteManager;

  // Town data
  private town: TownDefinition;
  private readonly TILE_SIZE = 16;

  // Player state
  private player: PlayerState;
  private characterSprite: CharacterSprite;
  private readonly MOVE_SPEED = 4;

  // NPC system
  private npcManager: NPCManager;
  private currentLocation: StoryLocation;

  // Input handling
  private inputCooldown: number = 0;
  private readonly INPUT_COOLDOWN_MS = 100;

  // Callbacks
  private onExitCallback: (() => void) | null = null;
  private onEnterBuildingCallback: ((building: Building) => void) | null = null;

  // Viewport for camera
  private viewportX: number = 0;
  private viewportY: number = 0;
  private readonly VIEWPORT_WIDTH = 256;
  private readonly VIEWPORT_HEIGHT = 240;

  constructor(
    ctx: CanvasRenderingContext2D,
    spriteManager: SpriteManager,
    town: TownDefinition,
    location: StoryLocation
  ) {
    this.renderer = new Renderer(ctx);
    this.spriteManager = spriteManager;
    this.town = town;
    this.currentLocation = location;

    // Initialize player at town entrance
    const entrance = town.exits[0]; // First exit is usually the entrance
    this.player = {
      x: entrance.x * this.TILE_SIZE + this.TILE_SIZE / 2,
      y: entrance.y * this.TILE_SIZE + this.TILE_SIZE / 2,
      tileX: entrance.x,
      tileY: entrance.y,
      facing: 'down',
      moving: false,
      moveProgress: 0,
    };

    // Create character sprite
    this.characterSprite = new CharacterSprite(spriteManager, {
      characterClass: 'FIGHTER',
      x: this.player.x,
      y: this.player.y,
      tileSize: this.TILE_SIZE,
    });

    // Initialize NPC manager
    this.npcManager = new NPCManager(spriteManager, location);
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    console.log(`[TownScene] Initialized in ${this.town.name}`);
    this.centerCameraOnPlayer();
  }

  update(deltaTime: number): void {
    // Update input cooldown
    if (!this.npcManager.isDialogueActive() && this.inputCooldown > 0) {
      this.inputCooldown -= deltaTime;
    }

    // Update movement
    if (!this.npcManager.isDialogueActive() && this.player.moving) {
      this.updateMovement(deltaTime);
    }

    // Update character sprite
    this.characterSprite.update(deltaTime);

    // Update NPCs
    this.npcManager.update(deltaTime);

    // Update camera
    this.centerCameraOnPlayer();
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Clear screen
    this.renderer.clear('#000000');

    // Render town tiles
    this.renderTown(ctx);

    // Render NPCs (behind player)
    this.npcManager.render(
      this.renderer,
      this.viewportX,
      this.viewportY,
      this.VIEWPORT_WIDTH,
      this.VIEWPORT_HEIGHT
    );

    // Render player
    this.renderPlayer(ctx);

    // Render town name (at bottom) or exit prompt
    if (isExit(this.town, this.player.tileX, this.player.tileY)) {
      this.renderer.drawText('Press Z to Exit', 128, 224, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ff88',
        align: 'center',
        shadow: true,
      });
    } else {
      this.renderer.drawText(this.town.name, 128, 224, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
        align: 'center',
        shadow: true,
      });
    }
  }

  handleInput(input: GameInput): void {
    // Let NPC manager handle input first (for dialogue)
    if (this.npcManager.handleInput(input)) {
      return;
    }

    // Don't process input while moving or on cooldown or during dialogue
    if (this.player.moving || this.inputCooldown > 0 || this.npcManager.isDialogueActive()) {
      return;
    }

    // Action button (A/Z) - interact with NPCs, enter buildings, or exit town
    if (input.justPressed.has(GameKey.A)) {
      // Try to exit first if on exit tile, otherwise interact
      if (isExit(this.town, this.player.tileX, this.player.tileY)) {
        if (this.onExitCallback) {
          this.onExitCallback();
        }
        return;
      }
      this.handleInteraction();
      return;
    }

    // Handle movement
    let dx = 0;
    let dy = 0;
    let newFacing: 'up' | 'down' | 'left' | 'right' | null = null;

    if (input.pressed.has(GameKey.UP)) {
      dy = -1;
      newFacing = 'up';
    } else if (input.pressed.has(GameKey.DOWN)) {
      dy = 1;
      newFacing = 'down';
    } else if (input.pressed.has(GameKey.LEFT)) {
      dx = -1;
      newFacing = 'left';
    } else if (input.pressed.has(GameKey.RIGHT)) {
      dx = 1;
      newFacing = 'right';
    }

    if (newFacing) {
      this.player.facing = newFacing;
    }

    if (dx !== 0 || dy !== 0) {
      this.tryMove(dx, dy);
    }
  }

  cleanup(): void {
    // Nothing to cleanup
  }

  // =========================================================================
  // Movement
  // =========================================================================

  private tryMove(dx: number, dy: number): void {
    const newTileX = this.player.tileX + dx;
    const newTileY = this.player.tileY + dy;

    // Check bounds
    if (newTileX < 0 || newTileX >= this.town.width || newTileY < 0 || newTileY >= this.town.height) {
      return;
    }

    // Check if NPC is blocking
    if (this.npcManager.isBlocked(newTileX, newTileY)) {
      // Try to interact with NPC
      this.npcManager.tryInteract(this.player.tileX, this.player.tileY, this.player.facing as Direction);
      return;
    }

    // Check if tile is walkable
    const tile = this.town.tiles[newTileY][newTileX];
    if (!isTownTileWalkable(tile)) {
      return;
    }

    // Start movement
    this.player.tileX = newTileX;
    this.player.tileY = newTileY;
    this.player.moving = true;
    this.player.moveProgress = 0;
    this.inputCooldown = this.INPUT_COOLDOWN_MS;

    // Update character sprite
    const direction: Direction = dx < 0 ? 'left' : dx > 0 ? 'right' : dy < 0 ? 'up' : 'down';
    const targetX = newTileX * this.TILE_SIZE;
    const targetY = newTileY * this.TILE_SIZE;
    this.characterSprite.moveTo(targetX, targetY, direction);
  }

  private updateMovement(_deltaTime: number): void {
    this.player.moveProgress += this.MOVE_SPEED;

    if (this.player.moveProgress >= this.TILE_SIZE) {
      // Movement complete
      this.player.moving = false;
      this.player.moveProgress = 0;
      this.updatePlayerPixelPosition();

      // Check if standing on exit
      if (isExit(this.town, this.player.tileX, this.player.tileY)) {
        // Show exit prompt or automatically exit
        console.log('[TownScene] Standing on exit. Press Z to leave.');
      }
    } else {
      // Interpolate position
      this.updatePlayerPixelPosition();
    }
  }

  private updatePlayerPixelPosition(): void {
    this.player.x = this.player.tileX * this.TILE_SIZE + this.TILE_SIZE / 2;
    this.player.y = this.player.tileY * this.TILE_SIZE + this.TILE_SIZE / 2;
  }

  // =========================================================================
  // Interaction
  // =========================================================================

  private handleInteraction(): void {
    // Calculate tile in front of player
    let checkX = this.player.tileX;
    let checkY = this.player.tileY;

    switch (this.player.facing) {
      case 'up':
        checkY -= 1;
        break;
      case 'down':
        checkY += 1;
        break;
      case 'left':
        checkX -= 1;
        break;
      case 'right':
        checkX += 1;
        break;
    }

    // Check for NPC interaction first
    const interacted = this.npcManager.tryInteract(
      this.player.tileX,
      this.player.tileY,
      this.player.facing as Direction
    );

    // If NPC was interacted with, don't process other interactions
    if (interacted || this.npcManager.isDialogueActive()) {
      return;
    }

    // Check for building entrance
    if (checkX >= 0 && checkX < this.town.width && checkY >= 0 && checkY < this.town.height) {
      const tile = this.town.tiles[checkY][checkX];
      if (isBuildingEntrance(tile)) {
        const building = getBuildingAt(this.town, checkX, checkY);
        if (building && this.onEnterBuildingCallback) {
          console.log(`[TownScene] Entering ${building.name}`);
          this.onEnterBuildingCallback(building);
        }
      }
    }

    // Check if standing on exit
    if (isExit(this.town, this.player.tileX, this.player.tileY)) {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    }
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  private renderTown(ctx: CanvasRenderingContext2D): void {
    // Calculate visible tile range
    const startCol = Math.floor(this.viewportX / this.TILE_SIZE);
    const endCol = Math.ceil((this.viewportX + this.VIEWPORT_WIDTH) / this.TILE_SIZE);
    const startRow = Math.floor(this.viewportY / this.TILE_SIZE);
    const endRow = Math.ceil((this.viewportY + this.VIEWPORT_HEIGHT) / this.TILE_SIZE);

    // Clamp to town bounds
    const clampedStartCol = Math.max(0, startCol);
    const clampedEndCol = Math.min(this.town.width, endCol);
    const clampedStartRow = Math.max(0, startRow);
    const clampedEndRow = Math.min(this.town.height, endRow);

    // Render tiles with enhanced graphics
    for (let row = clampedStartRow; row < clampedEndRow; row++) {
      for (let col = clampedStartCol; col < clampedEndCol; col++) {
        const tile = this.town.tiles[row][col];
        const screenX = col * this.TILE_SIZE - this.viewportX;
        const screenY = row * this.TILE_SIZE - this.viewportY;

        // Use enhanced tile rendering
        this.renderTile(ctx, tile, screenX, screenY);
      }
    }

    // Render exit markers (arrows pointing out)
    for (const exit of this.town.exits) {
      const screenX = exit.x * this.TILE_SIZE - this.viewportX;
      const screenY = exit.y * this.TILE_SIZE - this.viewportY;
      this.renderExitMarker(ctx, screenX, screenY, exit.direction);
    }

    // Render building entrance markers
    for (const building of this.town.buildings) {
      const screenX = building.x * this.TILE_SIZE - this.viewportX;
      const screenY = building.y * this.TILE_SIZE - this.viewportY;
      this.renderBuildingMarker(ctx, screenX, screenY, building.type);
    }
  }

  private renderExitMarker(ctx: CanvasRenderingContext2D, screenX: number, screenY: number, direction: string): void {
    const s = this.TILE_SIZE;
    const centerX = screenX + s / 2;
    const centerY = screenY + s / 2;

    // Draw pulsing green arrow pointing in exit direction
    ctx.fillStyle = '#00ff88';
    ctx.beginPath();

    switch (direction) {
      case 'down':
        ctx.moveTo(centerX, centerY + 6);
        ctx.lineTo(centerX - 4, centerY);
        ctx.lineTo(centerX + 4, centerY);
        break;
      case 'up':
        ctx.moveTo(centerX, centerY - 6);
        ctx.lineTo(centerX - 4, centerY);
        ctx.lineTo(centerX + 4, centerY);
        break;
      case 'left':
        ctx.moveTo(centerX - 6, centerY);
        ctx.lineTo(centerX, centerY - 4);
        ctx.lineTo(centerX, centerY + 4);
        break;
      case 'right':
        ctx.moveTo(centerX + 6, centerY);
        ctx.lineTo(centerX, centerY - 4);
        ctx.lineTo(centerX, centerY + 4);
        break;
    }
    ctx.closePath();
    ctx.fill();

    // Add glow effect
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private renderBuildingMarker(ctx: CanvasRenderingContext2D, screenX: number, screenY: number, buildingType: BuildingType): void {
    const s = this.TILE_SIZE;

    // Use colored glow around the door instead of icons to avoid overlap
    let glowColor = '';

    switch (buildingType) {
      case BuildingType.INN:
        glowColor = 'rgba(255, 182, 193, 0.6)'; // Pink glow for inn
        break;
      case BuildingType.WEAPON_SHOP:
        glowColor = 'rgba(192, 192, 192, 0.6)'; // Silver glow for weapons
        break;
      case BuildingType.ARMOR_SHOP:
        glowColor = 'rgba(65, 105, 225, 0.6)'; // Blue glow for armor
        break;
      case BuildingType.ITEM_SHOP:
        glowColor = 'rgba(50, 205, 50, 0.6)'; // Green glow for items
        break;
      case BuildingType.MAGIC_SHOP:
        glowColor = 'rgba(147, 112, 219, 0.6)'; // Purple glow for magic
        break;
      case BuildingType.CLINIC:
        glowColor = 'rgba(255, 99, 71, 0.6)'; // Red glow for clinic
        break;
      default:
        return; // Don't mark houses
    }

    // Draw a subtle glow around the door tile
    ctx.fillStyle = glowColor;
    ctx.fillRect(screenX - 2, screenY - 2, s + 4, s + 4);

    // Draw a small colored dot indicator at top-left of door (doesn't overlap with neighbors)
    const dotColors: Record<string, string> = {
      [BuildingType.INN]: '#FFB6C1',
      [BuildingType.WEAPON_SHOP]: '#C0C0C0',
      [BuildingType.ARMOR_SHOP]: '#4169E1',
      [BuildingType.ITEM_SHOP]: '#32CD32',
      [BuildingType.MAGIC_SHOP]: '#9370DB',
      [BuildingType.CLINIC]: '#FF6347',
    };

    const dotColor = dotColors[buildingType];
    if (dotColor) {
      // Draw small indicator dot
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(screenX + 3, screenY + 3, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = dotColor;
      ctx.beginPath();
      ctx.arc(screenX + 3, screenY + 3, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderTile(ctx: CanvasRenderingContext2D, tile: TownTile, screenX: number, screenY: number): void {
    const s = this.TILE_SIZE;

    switch (tile) {
      case TownTile.GRASS:
        ctx.fillStyle = '#228B22';
        ctx.fillRect(screenX, screenY, s, s);
        // Add grass texture
        ctx.fillStyle = '#1a7a1a';
        ctx.fillRect(screenX + 2, screenY + 3, 1, 1);
        ctx.fillRect(screenX + 8, screenY + 5, 1, 1);
        ctx.fillRect(screenX + 5, screenY + 10, 1, 1);
        ctx.fillRect(screenX + 12, screenY + 8, 1, 1);
        break;

      case TownTile.STONE_FLOOR:
        ctx.fillStyle = '#808080';
        ctx.fillRect(screenX, screenY, s, s);
        // Stone pattern
        ctx.strokeStyle = '#606060';
        ctx.lineWidth = 1;
        ctx.strokeRect(screenX + 1, screenY + 1, 7, 7);
        ctx.strokeRect(screenX + 8, screenY + 1, 7, 7);
        ctx.strokeRect(screenX + 1, screenY + 8, 7, 7);
        ctx.strokeRect(screenX + 8, screenY + 8, 7, 7);
        break;

      case TownTile.WOOD_FLOOR:
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX, screenY, s, s);
        // Wood grain
        ctx.fillStyle = '#6B3503';
        for (let i = 0; i < 16; i += 4) {
          ctx.fillRect(screenX, screenY + i, s, 1);
        }
        break;

      case TownTile.CARPET:
        ctx.fillStyle = '#8B0000';
        ctx.fillRect(screenX, screenY, s, s);
        // Carpet pattern
        ctx.fillStyle = '#aa2020';
        ctx.fillRect(screenX + 2, screenY + 2, 12, 12);
        ctx.fillStyle = '#cc4040';
        ctx.fillRect(screenX + 4, screenY + 4, 8, 8);
        break;

      case TownTile.WATER:
        ctx.fillStyle = '#4169E1';
        ctx.fillRect(screenX, screenY, s, s);
        // Wave highlights
        ctx.fillStyle = '#6495ED';
        ctx.fillRect(screenX + 2, screenY + 4, 4, 1);
        ctx.fillRect(screenX + 10, screenY + 8, 4, 1);
        break;

      case TownTile.WALL_STONE:
        ctx.fillStyle = '#696969';
        ctx.fillRect(screenX, screenY, s, s);
        // Stone blocks
        ctx.fillStyle = '#555555';
        ctx.fillRect(screenX, screenY, 8, 8);
        ctx.fillRect(screenX + 8, screenY + 8, 8, 8);
        ctx.fillStyle = '#777777';
        ctx.fillRect(screenX + 2, screenY + 2, 4, 4);
        ctx.fillRect(screenX + 10, screenY + 10, 4, 4);
        break;

      case TownTile.WALL_WOOD:
        ctx.fillStyle = '#654321';
        ctx.fillRect(screenX, screenY, s, s);
        // Wood planks
        ctx.fillStyle = '#503010';
        for (let i = 0; i < 16; i += 5) {
          ctx.fillRect(screenX + i, screenY, 1, s);
        }
        break;

      case TownTile.WALL_BRICK:
        ctx.fillStyle = '#A0522D';
        ctx.fillRect(screenX, screenY, s, s);
        // Brick pattern
        ctx.strokeStyle = '#704020';
        ctx.lineWidth = 1;
        for (let row = 0; row < 4; row++) {
          const offset = row % 2 === 0 ? 0 : 4;
          for (let col = 0; col < 3; col++) {
            ctx.strokeRect(screenX + offset + col * 8, screenY + row * 4, 8, 4);
          }
        }
        break;

      case TownTile.WALL_CASTLE:
        ctx.fillStyle = '#4F5F6F';
        ctx.fillRect(screenX, screenY, s, s);
        // Battlements
        ctx.fillStyle = '#3F4F5F';
        ctx.fillRect(screenX + 2, screenY, 4, 6);
        ctx.fillRect(screenX + 10, screenY, 4, 6);
        break;

      case TownTile.DOOR:
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX, screenY, s, s);
        // Door frame
        ctx.fillStyle = '#654321';
        ctx.fillRect(screenX + 2, screenY, 12, s);
        // Door handle
        ctx.fillStyle = '#FFD700';
        ctx.fillRect(screenX + 10, screenY + 8, 2, 2);
        break;

      case TownTile.STAIRS_UP:
        ctx.fillStyle = '#808080';
        ctx.fillRect(screenX, screenY, s, s);
        // Stairs
        ctx.fillStyle = '#606060';
        for (let i = 0; i < 4; i++) {
          ctx.fillRect(screenX + 2, screenY + 12 - i * 4, 12, 3);
        }
        break;

      case TownTile.STAIRS_DOWN:
        ctx.fillStyle = '#505050';
        ctx.fillRect(screenX, screenY, s, s);
        // Stairs going down
        ctx.fillStyle = '#303030';
        for (let i = 0; i < 4; i++) {
          ctx.fillRect(screenX + 2, screenY + i * 4, 12, 3);
        }
        break;

      case TownTile.COUNTER:
        ctx.fillStyle = '#8B4513'; // Wood floor behind (inside building)
        ctx.fillRect(screenX, screenY, s, s);
        // Counter
        ctx.fillStyle = '#DEB887';
        ctx.fillRect(screenX, screenY + 4, s, 10);
        ctx.fillStyle = '#BEA877';
        ctx.fillRect(screenX, screenY + 6, s, 2);
        // Add items on counter
        ctx.fillStyle = '#DAA520';
        ctx.fillRect(screenX + 3, screenY + 2, 3, 2);
        ctx.fillRect(screenX + 10, screenY + 2, 3, 2);
        break;

      case TownTile.TABLE:
        ctx.fillStyle = '#8B4513'; // Wood floor behind
        ctx.fillRect(screenX, screenY, s, s);
        // Table top
        ctx.fillStyle = '#CD853F';
        ctx.fillRect(screenX + 2, screenY + 4, 12, 8);
        // Table legs
        ctx.fillStyle = '#8B6914';
        ctx.fillRect(screenX + 3, screenY + 12, 2, 4);
        ctx.fillRect(screenX + 11, screenY + 12, 2, 4);
        break;

      case TownTile.CHAIR:
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX, screenY, s, s);
        // Chair
        ctx.fillStyle = '#D2691E';
        ctx.fillRect(screenX + 4, screenY + 8, 8, 6);
        ctx.fillRect(screenX + 4, screenY + 2, 8, 4);
        break;

      case TownTile.BED:
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX, screenY, s, s);
        // Bed
        ctx.fillStyle = '#FFB6C1';
        ctx.fillRect(screenX + 2, screenY + 2, 12, 12);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(screenX + 2, screenY + 2, 12, 4); // Pillow
        break;

      case TownTile.CHEST:
        ctx.fillStyle = '#808080'; // Stone floor behind (typically in castles/treasure rooms)
        ctx.fillRect(screenX, screenY, s, s);
        // Chest
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX + 3, screenY + 6, 10, 8);
        ctx.fillStyle = '#DAA520';
        ctx.fillRect(screenX + 6, screenY + 8, 4, 4); // Lock
        ctx.fillStyle = '#FFD700';
        ctx.fillRect(screenX + 7, screenY + 9, 2, 2); // Lock keyhole
        break;

      case TownTile.THRONE:
        ctx.fillStyle = '#8B0000';
        ctx.fillRect(screenX, screenY, s, s);
        // Throne
        ctx.fillStyle = '#FFD700';
        ctx.fillRect(screenX + 4, screenY + 8, 8, 6);
        ctx.fillRect(screenX + 4, screenY + 2, 8, 8);
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(screenX + 5, screenY + 3, 6, 5); // Cushion
        break;

      case TownTile.FOUNTAIN:
        ctx.fillStyle = '#228B22';
        ctx.fillRect(screenX, screenY, s, s);
        // Fountain
        ctx.fillStyle = '#808080';
        ctx.fillRect(screenX + 2, screenY + 4, 12, 10);
        ctx.fillStyle = '#87CEEB';
        ctx.fillRect(screenX + 4, screenY + 6, 8, 6);
        // Water spray
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(screenX + 7, screenY + 2, 2, 4);
        break;

      case TownTile.TREE:
        ctx.fillStyle = '#228B22';
        ctx.fillRect(screenX, screenY, s, s);
        // Tree trunk
        ctx.fillStyle = '#654321';
        ctx.fillRect(screenX + 6, screenY + 10, 4, 6);
        // Foliage
        ctx.fillStyle = '#006400';
        ctx.beginPath();
        ctx.arc(screenX + 8, screenY + 6, 6, 0, Math.PI * 2);
        ctx.fill();
        break;

      case TownTile.FLOWER:
        ctx.fillStyle = '#228B22';
        ctx.fillRect(screenX, screenY, s, s);
        // Flowers
        ctx.fillStyle = '#FF69B4';
        ctx.fillRect(screenX + 4, screenY + 4, 3, 3);
        ctx.fillRect(screenX + 10, screenY + 8, 3, 3);
        ctx.fillStyle = '#FFFF00';
        ctx.fillRect(screenX + 5, screenY + 5, 1, 1);
        ctx.fillRect(screenX + 11, screenY + 9, 1, 1);
        break;

      case TownTile.SIGN:
        ctx.fillStyle = '#228B22';
        ctx.fillRect(screenX, screenY, s, s);
        // Sign post
        ctx.fillStyle = '#654321';
        ctx.fillRect(screenX + 7, screenY + 8, 2, 8);
        // Sign board
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(screenX + 3, screenY + 4, 10, 6);
        break;

      default:
        ctx.fillStyle = '#FF00FF';
        ctx.fillRect(screenX, screenY, s, s);
    }
  }

  // Keep for backwards compatibility but unused
  private getTileColor(tile: TownTile): string {
    const colors: Record<TownTile, string> = {
      [TownTile.GRASS]: '#228B22',
      [TownTile.STONE_FLOOR]: '#808080',
      [TownTile.WOOD_FLOOR]: '#8B4513',
      [TownTile.CARPET]: '#8B0000',
      [TownTile.WATER]: '#4169E1',
      [TownTile.WALL_STONE]: '#696969',
      [TownTile.WALL_WOOD]: '#654321',
      [TownTile.WALL_BRICK]: '#A0522D',
      [TownTile.WALL_CASTLE]: '#2F4F4F',
      [TownTile.DOOR]: '#FFD700',
      [TownTile.STAIRS_UP]: '#FFFF00',
      [TownTile.STAIRS_DOWN]: '#FF8C00',
      [TownTile.COUNTER]: '#DEB887',
      [TownTile.TABLE]: '#CD853F',
      [TownTile.CHAIR]: '#D2691E',
      [TownTile.BED]: '#FFB6C1',
      [TownTile.CHEST]: '#DAA520',
      [TownTile.THRONE]: '#FFD700',
      [TownTile.FOUNTAIN]: '#87CEEB',
      [TownTile.TREE]: '#006400',
      [TownTile.FLOWER]: '#FF69B4',
      [TownTile.SIGN]: '#8B4513',
    };
    return colors[tile] ?? '#FF00FF';
  }

  private renderPlayer(_ctx: CanvasRenderingContext2D): void {
    const screenX = this.player.x - this.viewportX - 8;
    const screenY = this.player.y - this.viewportY - 8;

    // Render character sprite with shadow
    this.characterSprite.renderWithShadow(this.renderer, screenX, screenY);
  }

  private centerCameraOnPlayer(): void {
    // Center camera on player
    this.viewportX = this.player.x - this.VIEWPORT_WIDTH / 2;
    this.viewportY = this.player.y - this.VIEWPORT_HEIGHT / 2;

    // Clamp viewport to town bounds
    const maxX = this.town.width * this.TILE_SIZE - this.VIEWPORT_WIDTH;
    const maxY = this.town.height * this.TILE_SIZE - this.VIEWPORT_HEIGHT;

    this.viewportX = Math.max(0, Math.min(this.viewportX, maxX));
    this.viewportY = Math.max(0, Math.min(this.viewportY, maxY));

    // If town is smaller than viewport, center it
    if (this.town.width * this.TILE_SIZE < this.VIEWPORT_WIDTH) {
      this.viewportX = -((this.VIEWPORT_WIDTH - this.town.width * this.TILE_SIZE) / 2);
    }
    if (this.town.height * this.TILE_SIZE < this.VIEWPORT_HEIGHT) {
      this.viewportY = -((this.VIEWPORT_HEIGHT - this.town.height * this.TILE_SIZE) / 2);
    }
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Set the callback for when player exits town
   */
  onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  /**
   * Set the callback for when player enters a building
   */
  onEnterBuilding(callback: (building: Building) => void): void {
    this.onEnterBuildingCallback = callback;
  }

  /**
   * Set player position (in tiles)
   */
  setPosition(x: number, y: number): void {
    this.player.tileX = x;
    this.player.tileY = y;
    this.updatePlayerPixelPosition();
    this.characterSprite.setPosition(this.player.tileX * this.TILE_SIZE, this.player.tileY * this.TILE_SIZE);
    this.centerCameraOnPlayer();
  }

  /**
   * Set the character class for the player sprite
   */
  setCharacterClass(characterClass: string): void {
    this.characterSprite.setCharacterClass(characterClass);
  }

  /**
   * Get current town definition
   */
  getTown(): TownDefinition {
    return this.town;
  }

  /**
   * Get player position
   */
  getPosition(): { x: number; y: number } {
    return { x: this.player.tileX, y: this.player.tileY };
  }

  /**
   * Check if dialogue is active
   */
  isDialogueActive(): boolean {
    return this.npcManager.isDialogueActive();
  }

  /**
   * Load a new town (for reusing scene instance)
   */
  loadTown(town: TownDefinition, location: StoryLocation): void {
    console.log(`[TownScene] Loading town: ${town.name}`);
    this.town = town;
    this.currentLocation = location;

    // Reinitialize NPC manager for new location
    this.npcManager = new NPCManager(this.spriteManager, location);

    // Initialize player at town entrance
    const entrance = town.exits[0]; // First exit is usually the entrance
    this.player = {
      x: entrance.x * this.TILE_SIZE + this.TILE_SIZE / 2,
      y: entrance.y * this.TILE_SIZE + this.TILE_SIZE / 2,
      tileX: entrance.x,
      tileY: entrance.y,
      facing: 'down',
      moving: false,
      moveProgress: 0,
    };

    // Update character sprite position
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );

    // Center camera
    this.centerCameraOnPlayer();
  }
}
